import { DateTime } from "luxon";
import appstore from "../../appStore";
import { TaxiServiceId } from "../Condition/Redux/ConditionEntities";
import { FeatureFlags } from "../../Config/FeatureFlags";
import { BookingRequestV2, BookingLocationV2, BookingPayment, BookingTimingV2, BookingAccountV2 } from "../../Services/BookingEntitiesV2";
import { ClientDetails } from "../../Services/BookingEntities";
import { CreditCardPlaceholder, PaymentOptionKind } from "../Payment/PaymentEntities";
import { ShouldIncludeCardIdInBooking } from "../Payment/PaymentHandler";
import { UILayoutMode } from "../UILogicControl/UILogicControlEntities";
import { ComputeDriverNotes, GetBestLocationContact, GetOverallBookingContact } from "./CreateBookingCommon";
import { ConvertToApiModel } from "./Parcel/ParcelEntities";
import { ApplicationState } from "../../appState";
import { ServiceKind } from "../../utils/service-vehicles/ServiceMetadata";

/**
 * Build the V2 booking request payload for BookingApiV2 feature flag.
 * Uses TaxiServiceId instead of Conditions array.
 * Uses Locations array instead of PickupLocation/DropoffLocation.
 * Uses simplified Timing (only 2 fields).
 */
export function BuildBookingRequestV2(withSmsVerification: boolean): BookingRequestV2 {

    // Get data from the store, all booking information is guaranteed via validation before this step.
    const state = appstore.getState();
    const booking = state.booking;
    const condition = state.condition;
    const SelectedCondition = condition.SelectedCondition;

    // Get TaxiServiceId from the selected condition
    let taxiServiceId: number;
    
    // These items may not be returned from the GetServiceOfferings API
    if (!SelectedCondition.ApiVehicle || SelectedCondition.ApiVehicle.ApiVersion !== 2) {
        // Backwards compatibility: Allow "Next Available" to default to appropriate Sedan TaxiServiceId
        if (SelectedCondition.Service.kind === ServiceKind.NextAvailable) {
            taxiServiceId = TaxiServiceId.NextAvailable;
        } else if (SelectedCondition.Service.kind === ServiceKind.NextAvailableSouthAustralia) {
            taxiServiceId = TaxiServiceId.Taxi; // SedanNot13Limo for South Australia
        } else {
            throw new Error("BuildBookingRequestV2 requires a selected condition with ApiVersion 2 (TaxiServiceId)");
        }
    } else {
        taxiServiceId = SelectedCondition.ApiVehicle.ApiId;
    }

    // Evaluate booking time - V2 uses simplified timing with only Time and IsImmediate
    const timeV2 = booking.BookingTimeV2;
    const time: string = timeV2.IsImmediate 
        ? DateTime.now().toISO() 
        : timeV2.RequestedDate.toISO();

    const timingInfo: BookingTimingV2 = {
        Time: time,
        IsImmediate: timeV2.IsImmediate
    };

    // Build locations array
    const bookingLocations: BookingLocationV2[] = state.booking.Locations.map((location, index) => {
        return {
            PlaceId: location.Address!.GoogleMapsPlaceId,
            ContactDetails: GetBestLocationContact(location.Contact),
            DriverInstructions: index == 0 ? ComputeDriverNotes(booking) : location.DriverNotes,
            GeoLocation: location.CuratedLocationAddressId ? location.Address!.GeoLocation : null,
        }
    });

    // Determine NumberOfSeats
    // Only populate for Maxi Taxi (5), SilverServiceExec (9), and SilverServiceExec XL (10)
    const isMaxi = taxiServiceId === TaxiServiceId.MaxiTaxi;
    const isSilverExec = taxiServiceId === TaxiServiceId.SilverServiceExec;
    const isSilverExecXL = taxiServiceId === TaxiServiceId.SilverServiceExecXl;
    const numberOfSeats = (isMaxi || isSilverExec || isSilverExecXL) 
        ? (state.booking.NumberOfSeats ?? SelectedCondition.ApiVehicle!.MaxSeat) 
        : null;

    // Build payment info
    const threeDs = state.payment.ThreeDSecure;

    const paymentInfo: BookingPayment = {
        DeviceData: booking.DeviceData ?? null,
        PaymentCardId: null,
        AccountInfo: null,
        PaymentPreAuthOptIn: ShouldBePreAuthorised(state),
        IsFixedFare: false,
        FareEstimateId: null,
        Upgraded3DsNonce: threeDs?.Upgraded3DsNonce ?? null,
        LiabilityShifted3Ds: threeDs?.LiabilityShifted3Ds ?? null,
        ReceiptEmail: state.GuestPayment.EmailForReceipt,
    };

    /** Send fixed fare details with every booking request if available. */
    if (SelectedCondition.FareDetails) {
        paymentInfo.FareEstimateId = SelectedCondition.FareDetails.EstimateId;

        // Fixed Price is selected by default even if fare is not loaded. therefore make this true only if Fare details are available (and Fixed Price is still selected)
        paymentInfo.IsFixedFare = condition.IsPriceGuaranteeSelected;
    }

    if (ShouldIncludeCardIdInBooking(booking)) {
        paymentInfo.PaymentCardId = booking.PaymentOption?.Card?.CardId ?? null;
    }

    // Apple Pay
    if (booking.PaymentOption?.Type == "ApplePay") paymentInfo.PaymentCardId = state.payment.ApplePay!.CardId;

    // Google Pay
    if (booking.PaymentOption?.Type == "GooglePay") paymentInfo.PaymentCardId = state.payment.GooglePay!.CardId;

    // Guest credit card
    if (booking.PaymentOption === CreditCardPlaceholder) paymentInfo.PaymentCardId = state.payment.GuestCreditCard!.CardId;

    // Account booking
    const SelectedAccountData = appstore.getState().booking.AccountData;

    if (SelectedAccountData) {
        paymentInfo.AccountInfo = {
            AccountNumber: SelectedAccountData.SelectedAccount.AccountNumber,
            OrderNumber: SelectedAccountData.OrderNumber ?? null
        };
    }

    // Client info
    const clientInfo: ClientDetails = {
        IsMobile: state.uiLogicControl.LayoutMode === UILayoutMode.Mobile
    }

    // Build the V2 request
    const result: BookingRequestV2 = {
        TaxiServiceId: taxiServiceId,
        Locations: bookingLocations,
        ContactInfo: GetOverallBookingContact(),
        Payment: paymentInfo,
        NumberOfSeats: numberOfSeats,
        Timing: timingInfo,
        SmsChallengeId: null,
        Client: clientInfo,
        ParcelOptions: null,
        GoogleOdrdTripId: booking.GoogleOdrdTripId!, // not null here
    };

    // Parcel Delivery
    if (booking.DeliveryOption) {
        result.ParcelOptions = ConvertToApiModel(booking.DeliveryOption);
    }

    // SMS Verification
    if (withSmsVerification) {
        const smsChallengeId = appstore.getState().verification.SmsChallengeId!;
        result.SmsChallengeId = smsChallengeId;
    }

    return result;
}

/** Decides whether to make PaymentPreAuthOptIn flag true or false. */
function ShouldBePreAuthorised(state: ApplicationState): boolean {

    if (!FeatureFlags.PreAuth) return false;

    // Fixed fare details is required for pre-auth bookings.
    if (!state.condition.SelectedCondition.FareDetails) return false;

    // Only applicable for CNP bookings.
    if (state.booking.PaymentOption?.Kind !== PaymentOptionKind.CardOrWallet) return false;

    return true;
}
